'use strict';

var SELECTOR = {
    root: '[data-component="shop-similar-styles"]',
    track: '.shop-similar-styles__track',
    pagination: '.shop-similar-styles__pagination',
    pageLabel: '.shop-similar-styles__page-label',
    pagePrev: '.shop-similar-styles__page-btn--prev',
    pageNext: '.shop-similar-styles__page-btn--next',
    slide: '.shop-similar-styles__slide',
    availabilityMsg: '.availability-msg',
    inStock: '.is-in-stock'
};

var BREAKPOINT_DESKTOP = 992;
var SWIPE_THRESHOLD = 50;

/**
 * Returns the number of tiles visible per page based on current viewport width.
 * @returns {number} Tiles per page.
 */
function getTilesPerPage() {
    return window.innerWidth >= BREAKPOINT_DESKTOP ? 3 : 2;
}

/**
 * Returns the rendered width of a single slide including its gap.
 * @param {HTMLElement} track - The carousel track element.
 * @param {Array} slides - Array of slide elements.
 * @returns {number} Width in pixels.
 */
function getSlideWidth(track, slides) {
    if (!slides.length) return 0;
    var gap = parseFloat(window.getComputedStyle(track).gap) || 0;
    return slides[0].getBoundingClientRect().width + gap;
}

/**
 * Initialises carousel behaviour for a single root element.
 * @param {HTMLElement} root - The carousel root element.
 * @returns {void}
 */
function initInstance(root) {
    var track = root.querySelector(SELECTOR.track);
    if (!track) return;

    var slides = Array.from(root.querySelectorAll(SELECTOR.slide));
    if (!slides.length) return;

    var pagination = root.querySelector(SELECTOR.pagination);
    var pageLabel = root.querySelector(SELECTOR.pageLabel);
    var pagePrev = root.querySelector(SELECTOR.pagePrev);
    var pageNext = root.querySelector(SELECTOR.pageNext);

    var currentPage = 0;
    var tilesPerPage = getTilesPerPage();
    var isTransitioning = false;
    var touchStartX = 0;
    var touchStartY = 0;

    /**
     * Returns the total number of pages.
     * @returns {number} Page count.
     */
    function totalPages() {
        return Math.ceil(slides.length / tilesPerPage);
    }

    /**
     * Returns whether the carousel requires navigation controls.
     * @returns {boolean} True when slides exceed one page.
     */
    function isCarouselNeeded() {
        return slides.length > tilesPerPage;
    }

    /**
     * Applies the CSS transform to position the track at the current page.
     * @param {boolean} animate - Whether to apply a CSS transition.
     * @returns {void}
     */
    function updatePosition(animate) {
        track.style.transition = animate
            ? 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'none';
        track.style.transform = 'translateX(-' + (currentPage * tilesPerPage * getSlideWidth(track, slides)) + 'px)';
    }

    /**
     * Syncs arrow visibility, pagination label, and button disabled states
     * with the current page and viewport.
     * @returns {void}
     */
    function updateUI() {
        var pages = totalPages();
        var isCarouselActive = isCarouselNeeded();

        if (pageLabel) {
            pageLabel.textContent = (currentPage + 1) + ' of ' + pages;
        }

        if (pagePrev) pagePrev.disabled = (currentPage === 0);
        if (pageNext) pageNext.disabled = (currentPage >= pages - 1);

        if (pagination) pagination.classList.toggle('is-visible', isCarouselActive);
    }

    /**
     * Advances the carousel to the next page.
     * @returns {void}
     */
    function goNext() {
        if (isTransitioning || currentPage >= totalPages() - 1) return;
        isTransitioning = true;
        currentPage++;
        updatePosition(true);
        updateUI();
        setTimeout(function () { isTransitioning = false; }, 420);
    }

    /**
     * Returns the carousel to the previous page.
     * @returns {void}
     */
    function goPrev() {
        if (isTransitioning || currentPage <= 0) return;
        isTransitioning = true;
        currentPage--;
        updatePosition(true);
        updateUI();
        setTimeout(function () { isTransitioning = false; }, 420);
    }

    /**
     * Recalculates tiles per page, clamps the current page, and refreshes
     * position and UI. Called on init and on debounced resize.
     * @returns {void}
     */
    function setup() {
        tilesPerPage = getTilesPerPage();
        currentPage = Math.min(currentPage, Math.max(0, totalPages() - 1));
        updatePosition(false);
        updateUI();
    }

    track.addEventListener('touchstart', function (e) {
        touchStartX = e.changedTouches[0].screenX;
        touchStartY = e.changedTouches[0].screenY;
    }, { passive: true });

    track.addEventListener('touchmove', function (e) {
        if (document.querySelector('#quickViewModal.show')) {
            return;
        }
        var dx = Math.abs(e.changedTouches[0].screenX - touchStartX);
        var dy = Math.abs(e.changedTouches[0].screenY - touchStartY);
        if (dx > dy) {
            e.preventDefault();
        }
    }, { passive: false });

    track.addEventListener('touchend', function (e) {
        var diff = touchStartX - e.changedTouches[0].screenX;
        if (Math.abs(diff) > SWIPE_THRESHOLD) {
            if (diff > 0) {
                goNext();
            } else {
                goPrev();
            }
        }
    }, { passive: true });

    if (pageNext) pageNext.addEventListener('click', goNext);
    if (pagePrev) pagePrev.addEventListener('click', goPrev);

    var resizeTimer;
    window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(setup, 100);
    });

    setup();
}

/**
 * Returns true when the currently selected variant is unavailable.
 * Checks for the absence of an in-stock indicator inside the availability
 * message element — the same element the PDP updates on every variant change.
 * @returns {boolean} True when the selected variant is not available.
 */
function isVariantUnavailable() {
    var msg = document.querySelector(SELECTOR.availabilityMsg);
    if (!msg || msg.textContent.trim() === '') return false;
    return msg.querySelector(SELECTOR.inStock) === null;
}

/**
 * Adds or removes the active class on every carousel root based on whether
 * the selected product variant is currently unavailable.
 * @returns {void}
 */
function updateCarouselVisibility() {
    var show = isVariantUnavailable();
    document.querySelectorAll(SELECTOR.root).forEach(function (root) {
        root.classList.toggle('is-active', show);
    });
}

/**
 * Attaches a MutationObserver to the availability message element so that
 * carousel visibility is re-evaluated whenever the PDP updates it.
 * @returns {void}
 */
function observeAvailability() {
    var msg = document.querySelector(SELECTOR.availabilityMsg);
    if (!msg) return;
    var observer = new MutationObserver(updateCarouselVisibility);
    observer.observe(msg, { childList: true, subtree: true });
}

module.exports = {
    /**
     * Queries all carousel root elements on the page, initialises each one,
     * evaluates initial visibility, and begins observing availability changes.
     * @returns {void}
     */
    init: function () {
        var roots = document.querySelectorAll(SELECTOR.root);
        roots.forEach(initInstance);
        updateCarouselVisibility();
        observeAvailability();
    }
};
